import { useEffect, useState, type DependencyList, type ReactNode } from "react";
import { DatePicker as ZhDatePicker, DateRangePicker as ZhDateRangePicker } from "zhihao-ui-react";

export type DashboardDateValue = number | string | string[] | undefined;

/** 将看板快捷时间或自定义区间转换为旧版接口参数。 */
export function toDashboardDateParams(value: DashboardDateValue) {
    if (Array.isArray(value) && value.length > 0) {
        return { beginTime: value[0], endTime: value[1] };
    }
    if (typeof value === "number") {
        return { scope: value };
    }
    if (typeof value === "string" && value.length > 0 && Number.isFinite(Number(value))) {
        return { scope: Number(value) };
    }
    return {};
}

interface DashboardDateSelectProps {
    value: DashboardDateValue;
    onChange: (value: DashboardDateValue) => void;
    options?: string[];
    defaultActive?: string;
    pickerType?: "year" | "month" | "daterange";
}

const DATE_SHORTCUTS = [
    { code: "all", label: "全部", value: 1 },
    { code: "week-4", label: "近四周", value: 2 },
    { code: "month", label: "近一月", value: 2 },
    { code: "quarter", label: "近一季度", value: 3 },
    { code: "year", label: "近一年", value: 4 }
] as const;

/** React 版日期筛选，保留 Vue 组件的快捷选项和接口值。 */
export function DashboardDateSelect({
    value,
    onChange,
    options = ["all"],
    defaultActive = "",
    pickerType = "daterange"
}: DashboardDateSelectProps) {
    const [active, setActive] = useState(defaultActive);
    const visibleShortcuts = DATE_SHORTCUTS.filter(item => options.includes(item.code));
    // 组件要求固定二元组，避免普通 string[] 被错误识别为不完整日期范围。
    const rangeValue: [string | null, string | null] | undefined = Array.isArray(value)
        ? [value[0] || null, value[1] || null]
        : undefined;
    return (
        <div className="date-select">
            {visibleShortcuts.length > 0 ? (
                <div className="options">
                    {visibleShortcuts.map(item => (
                        <button
                            key={item.code}
                            type="button"
                            className={active === item.code ? "item active" : "item"}
                            onClick={() => {
                                setActive(item.code);
                                onChange(item.value);
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            ) : null}
            {pickerType === "daterange" ? (
                <ZhDateRangePicker
                    aria-label="自定义日期范围"
                    clearable={false}
                    format="YYYY-MM-DD"
                    valueFormat="YYYY-MM-DD"
                    startPlaceholder="开始日期"
                    endPlaceholder="结束日期"
                    rangeSeparator="-"
                    onChange={(_, formattedValue) => {
                        setActive("");
                        onChange(formattedValue || undefined);
                    }}
                    value={rangeValue}
                />
            ) : (
                <div className="dashboard-single-date-picker">
                    <ZhDatePicker
                        aria-label={pickerType === "month" ? "选择月份" : "选择年份"}
                        clearable={false}
                        format={pickerType === "month" ? "YYYY年MM月" : "YYYY年"}
                        placeholder={pickerType === "month" ? "请选择月份" : "请选择年份"}
                        type={pickerType}
                        value={typeof value === "string" ? value : undefined}
                        valueFormat={pickerType === "month" ? "YYYY-MM" : "YYYY"}
                        onChange={(_, formattedValue) => {
                            setActive("");
                            onChange(formattedValue || undefined);
                        }}
                    />
                </div>
            )}
        </div>
    );
}

interface DataWrapProps {
    title: string;
    option?: ReactNode;
    children: ReactNode;
    className?: string;
}

/** 看板卡片容器，保持旧版标题、筛选器和内容区层次。 */
export function DataWrap({ title, option, children, className = "" }: DataWrapProps) {
    return (
        <section className={`data-wrap ${className}`}>
            <header className="data-wrap-header">
                <h2 className="data-wrap-header-title">{title}</h2>
                <div className="data-wrap-header-option">{option}</div>
            </header>
            <div className="data-wrap-main">{children}</div>
        </section>
    );
}

export interface DashboardMetric {
    label: string;
    value: unknown;
    unit?: string;
}

const displayValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "0";
    return typeof value === "number" ? value.toLocaleString("zh-CN") : String(value);
};

/** 统计指标网格，统一处理接口空值和千分位显示。 */
export function MetricGrid({ metrics }: { metrics: DashboardMetric[] }) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {metrics.map(metric => (
                <div key={metric.label} className="rounded-lg bg-[#f5f8ff] px-4 py-5">
                    <div className="text-sm text-[#666]">{metric.label}</div>
                    <div className="mt-2 text-2xl font-semibold text-[#222]">
                        {displayValue(metric.value)}
                        {metric.unit ? <span className="ml-1 text-sm font-normal">{metric.unit}</span> : null}
                    </div>
                </div>
            ))}
        </div>
    );
}

/** 将接口趋势数组绘制为轻量柱形图，避免依赖 Vue/ECharts 组件。 */
export function TrendBars({ values, labels }: { values: unknown[]; labels?: string[] }) {
    const numericValues = values.map(value => Number(value) || 0);
    const max = Math.max(...numericValues, 1);
    return (
        <div className="flex h-48 items-end gap-2 overflow-x-auto border-b border-[#e5e7eb] px-2 pb-2">
            {numericValues.length === 0 ? (
                <span className="self-center text-sm text-[#999]">暂无数据</span>
            ) : (
                numericValues.map((value, index) => (
                    <div
                        key={`${labels?.[index] || index}`}
                        className="flex min-w-10 flex-1 flex-col items-center gap-1"
                    >
                        <span className="text-xs text-[#666]">{displayValue(value)}</span>
                        <div
                            className="w-full min-w-3 rounded-t bg-[#3370ff]"
                            style={{ height: `${Math.max((value / max) * 130, value > 0 ? 4 : 0)}px` }}
                        />
                        <span className="max-w-20 truncate text-[11px] text-[#999]">
                            {labels?.[index] || index + 1}
                        </span>
                    </div>
                ))
            )}
        </div>
    );
}

export interface DashboardRankItem {
    name: string;
    value: number;
}

/** 热门航线/目的地排行，保留旧版排序、次数和比例进度。 */
export function RankList({ items }: { items: DashboardRankItem[] }) {
    const max = Math.max(...items.map(item => item.value), 1);
    return items.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#999]">暂无数据</div>
    ) : (
        <div className="space-y-4">
            {items.map((item, index) => (
                <div key={`${item.name}-${index}`}>
                    <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate">
                            <b className="mr-3 inline-flex h-5 w-5 items-center justify-center rounded bg-[#f0f2f5] text-xs">
                                {index + 1}
                            </b>
                            {item.name}
                        </span>
                        <span className="shrink-0 text-[#666]">{displayValue(item.value)}次</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded bg-[#edf2ff]">
                        <div
                            className="h-full rounded bg-[#3370ff]"
                            style={{ width: `${(item.value / max) * 100}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** 通用异步看板数据状态，统一处理卸载和接口失败。 */
export function useDashboardQuery<T>(loader: () => Promise<T>, deps: DependencyList) {
    const [data, setData] = useState<T>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    useEffect(() => {
        let disposed = false;
        setLoading(true);
        void loader()
            .then(result => {
                if (!disposed) {
                    setData(result);
                    setError("");
                }
            })
            .catch(() => {
                if (!disposed) setError("数据加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
        // 调用方提供完整依赖，保持旧看板切换日期后重新请求。
    }, deps);
    return { data, loading, error };
}

/** 将加载状态、错误和内容组合为稳定的看板内容区。 */
export function WidgetBody({ loading, error, children }: { loading: boolean; error?: string; children: ReactNode }) {
    if (loading) return <div className="py-8 text-center text-sm text-[#999]">加载中…</div>;
    if (error) return <div className="py-8 text-center text-sm text-[#d4380d]">{error}</div>;
    return <>{children}</>;
}

interface StatisticWidgetProps {
    title: string;
    value: DashboardDateValue;
    onChange: (value: DashboardDateValue) => void;
    options?: string[];
    defaultActive?: string;
    loading: boolean;
    error?: string;
    metrics: DashboardMetric[];
}

/** 统计型看板子卡片，统一承接日期筛选、请求状态和指标展示。 */
export function StatisticWidget({
    title,
    value,
    onChange,
    options,
    defaultActive,
    loading,
    error,
    metrics
}: StatisticWidgetProps) {
    return (
        <DataWrap
            title={title}
            option={
                <DashboardDateSelect
                    value={value}
                    onChange={onChange}
                    options={options}
                    defaultActive={defaultActive}
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <MetricGrid metrics={metrics} />
            </WidgetBody>
        </DataWrap>
    );
}
